import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { Loader } from "../components/KpiCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileDown, Loader2, Send } from "lucide-react";

function Row({ label, value, bold, indent }) {
  return (
    <div className={`flex items-center justify-between py-2.5 border-b border-stone-100 ${bold ? "font-semibold text-[#1A1A1A]" : "text-[#3a3a34]"} ${indent ? "pl-4" : ""}`}>
      <span className={bold ? "" : "text-sm"}>{label}</span>
      <span className="font-mono tabular">{fmtUSD(value)}</span>
    </div>
  );
}

export default function Reports() {
  const [soa, setSoa] = useState(null);
  const [pos, setPos] = useState(null);
  const [packetBusy, setPacketBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/statement-of-activities").then((r) => setSoa(r.data));
    api.get("/reports/financial-position").then((r) => setPos(r.data));
  }, []);

  const downloadPacket = async () => {
    setPacketBusy(true);
    try {
      const res = await api.get("/reports/board-packet", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "DHHF_Board_Packet.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Board packet downloaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPacketBusy(false);
    }
  };

  if (!soa || !pos) return <Loader />;

  const emailTrustees = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/reports/send-board-packet");
      toast.success(`Packet emailed to ${data.sent.length} trustee(s)`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Board-ready nonprofit financial statements."
        action={
          <div className="flex items-center gap-3">
            <button onClick={emailTrustees} disabled={emailBusy} data-testid="email-trustees-btn"
              className="bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2 disabled:opacity-60">
              {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Email to Trustees
            </button>
            <button onClick={downloadPacket} disabled={packetBusy} data-testid="board-packet-btn"
              className="bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2 disabled:opacity-60">
              {packetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              AI Board Packet (PDF)
            </button>
          </div>
        } />
      <Tabs defaultValue="activities">
        <TabsList className="mb-6">
          <TabsTrigger value="activities" data-testid="tab-activities">Statement of Activities</TabsTrigger>
          <TabsTrigger value="position" data-testid="tab-position">Statement of Financial Position</TabsTrigger>
        </TabsList>

        <TabsContent value="activities">
          <div className="bg-white border border-stone-200 rounded-xl p-8 max-w-2xl" data-testid="report-activities">
            <h2 className="font-heading text-xl mb-1">Statement of Activities</h2>
            <p className="text-xs text-[#5C5C54] mb-6">Fiscal Year to Date · David's Harmony House Foundation</p>
            <div className="text-xs uppercase tracking-wider text-[#5C5C54] mb-1">Revenue & Support</div>
            {soa.revenue.map((r) => <Row key={r.category} label={r.category} value={r.amount} indent />)}
            <Row label="Total Revenue" value={soa.total_revenue} bold />
            <div className="text-xs uppercase tracking-wider text-[#5C5C54] mb-1 mt-6">Expenses</div>
            {soa.expenses.map((r) => <Row key={r.category} label={r.category} value={r.amount} indent />)}
            <Row label="Total Expenses" value={soa.total_expenses} bold />
            <div className="mt-4 pt-2 flex items-center justify-between font-semibold text-lg">
              <span>Change in Net Assets</span>
              <span className={`font-mono ${soa.change_in_net_assets >= 0 ? "text-inflow" : "text-outflow"}`}>{fmtUSD(soa.change_in_net_assets)}</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="position">
          <div className="bg-white border border-stone-200 rounded-xl p-8 max-w-2xl" data-testid="report-position">
            <h2 className="font-heading text-xl mb-1">Statement of Financial Position</h2>
            <p className="text-xs text-[#5C5C54] mb-6">As of Today · David's Harmony House Foundation</p>
            <div className="text-xs uppercase tracking-wider text-[#5C5C54] mb-1">Assets</div>
            <Row label="Cash & Equivalents" value={pos.assets.cash_and_equivalents} indent />
            <Row label="Total Assets" value={pos.assets.total_assets} bold />
            <div className="text-xs uppercase tracking-wider text-[#5C5C54] mb-1 mt-6">Liabilities</div>
            <Row label="Accounts Payable" value={pos.liabilities.accounts_payable} indent />
            <Row label="Total Liabilities" value={pos.liabilities.total_liabilities} bold />
            <div className="text-xs uppercase tracking-wider text-[#5C5C54] mb-1 mt-6">Net Assets</div>
            {Object.entries(pos.net_assets).map(([k, v]) => <Row key={k} label={k} value={v} indent />)}
            <Row label="Total Net Assets" value={pos.total_net_assets} bold />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
